import { forwardRef } from 'react';
import { TextField, Tooltip, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';

import { FormSection } from '@/components/form-elements/FormSection';
import { Organization, Sector, UpdateOrganizationDto } from '@/services/api';
import { TextFieldLoading } from '@/components/form-elements/TextFieldLoading';
import { useIsFieldDisabled } from '@/hooks/useIsFieldDisabled';
import { validateAsOptionalGtOne } from '@/utils/validateAsOptionalGtOne';
import { TextFieldText } from '@/components/form-elements/TextFieldText';
import { TextFieldNumber } from '@/components/form-elements/TextFieldNumber';

export type FormPartOrganizationCoreData = Pick<
  UpdateOrganizationDto,
  'name' | 'consultingCaseId' | 'size' | 'sector' | 'fileStorage'
> & {
  participationDate: Dayjs | null;
};

type CoreDataFormProps = {
  organization?: Organization;
  isLoading: boolean;
};

export const CoreDataForm = forwardRef<HTMLElement, CoreDataFormProps>(
  ({ organization, isLoading }: CoreDataFormProps, ref) => {
    const {
      register,
      formState: { errors },
      watch,
    } = useFormContext<FormPartOrganizationCoreData>();
    const participationDateValue = watch('participationDate');

    const { t } = useTranslation();
    const isFieldDisabled = useIsFieldDisabled(organization);

    return (
      <FormSection ref={ref} title="Stammdaten">
        <TextFieldText
          label="Organisationsname"
          isLoading={isLoading}
          name="name"
          required
          disabled={isFieldDisabled('name')}
        />

        <TextFieldLoading isLoading={isLoading}>
          <Controller
            name="participationDate"
            render={({ field: { ref: participationDateRef, ...rest } }) => (
              <DatePicker
                label="Teilnahmejahr"
                format="YYYY"
                views={['year']}
                disableFuture
                inputRef={participationDateRef}
                disabled={isFieldDisabled('participationDate')}
                {...rest}
              />
            )}
          />
        </TextFieldLoading>

        {!isFieldDisabled('consultingCaseId') && (
          <TextFieldLoading isLoading={isLoading}>
            <Controller<FormPartOrganizationCoreData, 'consultingCaseId'>
              name="consultingCaseId"
              defaultValue=""
              rules={{
                minLength: { value: 3, message: 'Bitte geben sie eine dreistellige Nummer ein' },
                maxLength: { value: 3, message: 'Bitte geben sie eine dreistellige Nummer ein' },
              }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    onChange={(event) => {
                      field.onChange(event.currentTarget.value.replace(/\D/g, ''));
                    }}
                    label="Beratungsfall-Nummer"
                    inputProps={{ maxLength: 3 }}
                    error={!!errors.consultingCaseId}
                    helperText={errors?.consultingCaseId?.message}
                  />
                  <Tooltip
                    title="Zusammensetzung: Beratungsfallnummer (3) + Teilnahmejahr (2) + Anwendungsfall-Nummerierung (2)"
                    placement="top-start"
                  >
                    <Typography sx={{ paddingLeft: '16px', paddingBottom: '10px' }}>
                      {field.value?.length === 3 &&
                        `Referenznummer: ${field.value || 'XXX'}${
                          typeof participationDateValue?.format === 'function'
                            ? participationDateValue?.format('YY')
                            : 'JJ'
                        }10`}
                    </Typography>
                  </Tooltip>
                </>
              )}
            />
          </TextFieldLoading>
        )}

        <TextFieldNumber
          label="Anzahl der Beschäftigten in der Organisation"
          isLoading={isLoading}
          name="size"
          rules={{ min: 1, validate: validateAsOptionalGtOne }}
          disabled={isFieldDisabled('size')}
        />

        <TextFieldLoading isLoading={isLoading}>
          <TextField label="Branche" select {...register('sector')} disabled={isFieldDisabled('sector')}>
            {Object.values(Sector).map((sector) => (
              <option key={sector} value={sector}>
                {t(`Sector.${sector}`)}
              </option>
            ))}
          </TextField>
        </TextFieldLoading>

        <TextFieldText
          label="Link zur Dokumentenablage"
          isLoading={isLoading}
          name="fileStorage"
          disabled={isFieldDisabled('fileStorage')}
        />
      </FormSection>
    );
  },
);
